import {
  ANALYTICS_EVENT_PROPERTY_CONTRACTS,
  ANALYTICS_EVENT_NAMES,
  type AnalyticsEventName,
  type AnalyticsPropertyValue,
} from './types';

const CANONICAL_ANALYTICS_KEY = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

export const ANALYTICS_SERVER_CONTEXT_PROPERTIES = [
  'distinct_id',
  'firm_id',
  'app_surface',
  'environment',
  'is_advisor',
] as const;

export const ANALYTICS_SENSITIVE_BEHAVIORAL_PROPERTIES = [
  'email',
  'email_address',
  'given_name',
  'family_name',
  'full_name',
  'name',
  'task_body',
  'message_body',
  'message_text',
  'body',
  'text',
  'content',
  'query_text',
  'password',
  'access_token',
  'refresh_token',
  'client_id',
  'household_id',
  'person_id',
  'account_id',
  'invitation_id',
  'phone',
  'phone_number',
  'address',
  'note',
  'notes',
  'subject',
  'description',
  'error',
  'error_message',
  'amount',
  'balance',
  'income',
  'spending',
  'net_worth',
] as const;

export type AnalyticsValidationFailureReason =
  | 'invalid_event_name'
  | 'invalid_property_name'
  | 'invalid_property_value'
  | 'missing_property'
  | 'unknown_property'
  | 'reserved_property'
  | 'sensitive_property';

export type AnalyticsValidationResult =
  | { ok: true }
  | {
      ok: false;
      reason: AnalyticsValidationFailureReason;
      key: string;
    };

const serverContextProperties = new Set<string>(ANALYTICS_SERVER_CONTEXT_PROPERTIES);
const sensitiveBehavioralProperties = new Set<string>(ANALYTICS_SENSITIVE_BEHAVIORAL_PROPERTIES);
const canonicalAnalyticsEventNames = new Set<string>(ANALYTICS_EVENT_NAMES);
const legacyReservedPropertyKeys = new Set<string>(['account_type', 'conversation_state']);
export interface AnalyticsServerContext {
  distinct_id: string;
  firm_id: string;
  app_surface: 'advisor' | 'client';
  environment: string;
  is_advisor: boolean;
}

export type AnalyticsLegacyPropertyRule = 'boolean' | 'number' | readonly (string | number | boolean | null)[];
export type AnalyticsLegacyEventPropertyContracts = Readonly<
  Record<string, Readonly<Record<string, AnalyticsLegacyPropertyRule>>>
>;

const normalizedPropertyKey = (key: string): string =>
  key
    .replace(/^\$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();

const isAnalyticsPropertyValue = (value: unknown): value is AnalyticsPropertyValue => {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') {
    return true;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (!Array.isArray(value)) {
    return false;
  }

  const firstValueType = value.length ? typeof value[0] : undefined;
  return value.every(
    (item) =>
      (typeof item === 'boolean' ||
        typeof item === 'string' ||
        (typeof item === 'number' && Number.isFinite(item))) &&
      typeof item === firstValueType
  );
};

const containsSensitiveFreeformText = (value: string): boolean =>
  /\r|\n|[/?=&]/.test(value) ||
  /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/.test(value) ||
  /^[a-z0-9]{20,}$/i.test(value);

const isPrivacySafeAnalyticsPropertyValue = (value: unknown): value is AnalyticsPropertyValue => {
  if (!isAnalyticsPropertyValue(value)) {
    return false;
  }

  if (typeof value === 'string') {
    return !containsSensitiveFreeformText(value);
  }

  return !Array.isArray(value) || value.every((item) => typeof item !== 'string' || !containsSensitiveFreeformText(item));
};

const isAllowedLegacyPropertyValue = (value: AnalyticsPropertyValue, rule: AnalyticsLegacyPropertyRule): boolean => {
  if (rule === 'boolean') {
    return typeof value === 'boolean';
  }

  if (rule === 'number') {
    return typeof value === 'number' && Number.isFinite(value);
  }

  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return rule.includes(value);
  }

  return false;
};

const isReservedProperty = (key: string): boolean => serverContextProperties.has(normalizedPropertyKey(key));

const isSensitiveProperty = (key: string): boolean =>
  sensitiveBehavioralProperties.has(normalizedPropertyKey(key)) ||
  /_(?:id|email|body|content|token|password|amount|balance)$/.test(normalizedPropertyKey(key));

export const isCanonicalAnalyticsEventName = (eventName: string): eventName is AnalyticsEventName =>
  canonicalAnalyticsEventNames.has(eventName);

export const validateAnalyticsEventInput = (
  eventName: string,
  params: Readonly<Record<string, unknown>> = {}
): AnalyticsValidationResult => {
  if (!CANONICAL_ANALYTICS_KEY.test(eventName)) {
    return { ok: false, reason: 'invalid_event_name', key: eventName };
  }

  for (const key of Object.keys(params)) {
    if (!CANONICAL_ANALYTICS_KEY.test(key)) {
      return { ok: false, reason: 'invalid_property_name', key };
    }

    if (isReservedProperty(key)) {
      return { ok: false, reason: 'reserved_property', key };
    }

    if (isSensitiveProperty(key)) {
      return { ok: false, reason: 'sensitive_property', key };
    }

    if (!isAnalyticsPropertyValue(params[key])) {
      return { ok: false, reason: 'invalid_property_value', key };
    }
  }

  if (!isCanonicalAnalyticsEventName(eventName)) {
    return { ok: false, reason: 'invalid_event_name', key: eventName };
  }

  if (isCanonicalAnalyticsEventName(eventName)) {
    const contract = ANALYTICS_EVENT_PROPERTY_CONTRACTS[eventName];

    for (const [key, allowedValues] of Object.entries(contract)) {
      if (!(key in params)) {
        return { ok: false, reason: 'missing_property', key };
      }

      if (!(allowedValues as readonly unknown[]).includes(params[key])) {
        return { ok: false, reason: 'invalid_property_value', key };
      }
    }

    for (const key of Object.keys(params)) {
      if (!(key in contract)) {
        return { ok: false, reason: 'unknown_property', key };
      }
    }
  }

  return { ok: true };
};

export const prepareAnalyticsEventProperties = ({
  eventName,
  legacyEventPropertyContracts,
  params = {},
  context,
}: {
  eventName?: string;
  legacyEventPropertyContracts?: AnalyticsLegacyEventPropertyContracts;
  params?: Readonly<Record<string, unknown>>;
  context: AnalyticsServerContext;
}): {
  properties: Record<string, AnalyticsPropertyValue | undefined>;
  droppedProperties: string[];
} => {
  const properties: Record<string, AnalyticsPropertyValue | undefined> = {};
  const droppedProperties: string[] = [];
  const legacyPropertyContract = eventName ? legacyEventPropertyContracts?.[eventName] : undefined;
  const canonicalPropertyContract =
    eventName && isCanonicalAnalyticsEventName(eventName) ? ANALYTICS_EVENT_PROPERTY_CONTRACTS[eventName] : undefined;

  for (const [key, value] of Object.entries(params)) {
    const legacyPropertyRule = legacyPropertyContract?.[key];
    if (
      isReservedProperty(key) ||
      isSensitiveProperty(key) ||
      (!canonicalPropertyContract && legacyReservedPropertyKeys.has(normalizedPropertyKey(key))) ||
      (!canonicalPropertyContract && !legacyPropertyRule) ||
      (canonicalPropertyContract &&
        (!(key in canonicalPropertyContract) ||
          !(canonicalPropertyContract[key as keyof typeof canonicalPropertyContract] as readonly unknown[]).includes(
            value
          ))) ||
      !isPrivacySafeAnalyticsPropertyValue(value) ||
      (legacyPropertyRule && !isAllowedLegacyPropertyValue(value, legacyPropertyRule))
    ) {
      droppedProperties.push(key);
      continue;
    }

    properties[key] = value;
  }

  return {
    properties: { ...properties, ...context },
    droppedProperties,
  };
};

export const isAnalyticsEventProperties = (value: unknown): value is Readonly<Record<string, unknown>> =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

export const isAnalyticsFirmIgnored = (firmId: string | undefined, ignoredFirms: string | undefined): boolean => {
  if (!firmId) {
    return false;
  }

  return (ignoredFirms ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .includes(firmId);
};
